/**
 * 브리핑 화면 네 곳이 공통으로 쓰는 로딩·에러·빈 상태.
 *
 * 목 데이터로만 개발하면 이 세 상태를 한 번도 안 보게 됩니다.
 * 그런데 실서버에서는 매일 아침 첫 진입이 로딩이고,
 * 신규 센터는 계속 빈 상태이며, 네트워크가 끊기면 에러입니다.
 * 여기가 허술하면 흰 화면이 뜹니다.
 */

const BORDER = '#E3EAF3';

/** 뼈대 한 조각 — 회색 블록이 은은하게 숨 쉬는 효과 */
const Bone = ({ h, w, r = 10 }: { h: number; w?: number | string; r?: number }) => (
    <div
        style={{
            height: h,
            width: w ?? '100%',
            borderRadius: r,
            background: 'linear-gradient(90deg,#EEF1F6 25%,#F6F8FB 50%,#EEF1F6 75%)',
            backgroundSize: '200% 100%',
            animation: 'opsShimmer 1.4s ease-in-out infinite',
        }}
    />
);

/** 애니메이션 키프레임 — styled-components 없이 한 번만 심는다 */
const Keyframes = () => (
    <style>{`@keyframes opsShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
);

/**
 * 로딩 뼈대.
 * 스피너 대신 실제 레이아웃 모양을 보여준다 — 화면이 어떻게 채워질지 미리 알면
 * 같은 대기 시간도 짧게 느껴진다.
 */
export const OpsLoading = ({ variant = 'page' }: { variant?: 'page' | 'list' | 'panel' }) => (
    <div className="flex w-full flex-col" style={{ gap: 14 }}>
        <Keyframes />
        {variant === 'page' && (
            <>
                <Bone h={64} r={12} />
                <Bone h={132} r={16} />
                <div className="flex" style={{ gap: 12 }}>
                    <Bone h={120} r={14} />
                    <Bone h={120} r={14} />
                    <Bone h={120} r={14} />
                    <Bone h={120} r={14} />
                </div>
                <Bone h={280} r={16} />
            </>
        )}
        {variant === 'list' && (
            <>
                <Bone h={56} r={12} />
                <Bone h={168} r={14} />
                <Bone h={168} r={14} />
            </>
        )}
        {variant === 'panel' && <Bone h={220} r={14} />}
        <span className="sr-only" role="status" aria-live="polite">
            불러오는 중입니다
        </span>
    </div>
);

/**
 * 에러 상태.
 * "오류가 발생했습니다"만 띄우면 대표님은 할 수 있는 게 없다.
 * 다시 시도 버튼과, 계속 안 되면 어떻게 하라는 안내를 같이 준다.
 */
export const OpsError = ({
    title = '데이터를 불러오지 못했습니다',
    onRetry,
    detail,
}: {
    title?: string;
    onRetry?: () => void;
    detail?: string;
}) => (
    <div
        className="flex w-full flex-col items-center rounded-[16px]"
        style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, padding: '56px 24px' }}
        role="alert"
    >
        <span style={{ fontSize: 34 }}>🔌</span>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#16202E', marginTop: 14 }}>{title}</span>
        <span
            style={{ fontSize: 14, color: '#7C8794', marginTop: 8, textAlign: 'center', lineHeight: '21px' }}
        >
            잠시 후 다시 시도해 주세요.
            <br />
            계속 안 되면 라포 고객센터로 알려주시면 바로 확인하겠습니다.
        </span>
        {detail && (
            <span style={{ fontSize: 12, color: '#B4BDC9', marginTop: 10 }}>({detail})</span>
        )}
        {onRetry && (
            <button
                type="button"
                onClick={onRetry}
                className="rounded-[10px]"
                style={{
                    marginTop: 18,
                    height: 44,
                    padding: '0 24px',
                    background: '#1A305D',
                    border: 'none',
                    color: '#FFFFFF',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                }}
            >
                다시 불러오기
            </button>
        )}
    </div>
);

/**
 * 빈 상태.
 * 라포를 오늘 도입한 센터는 여기서 첫인상이 결정된다.
 * "데이터 없음"이 아니라 "언제부터 보인다"를 말해준다.
 */
export const OpsEmpty = ({
    icon = '📋',
    title,
    body,
    hint,
}: {
    icon?: string;
    title: string;
    body?: string;
    hint?: string;
}) => (
    <div
        className="flex w-full flex-col items-center rounded-[16px]"
        style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, padding: '56px 24px' }}
    >
        <span style={{ fontSize: 34 }}>{icon}</span>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#16202E', marginTop: 14 }}>{title}</span>
        {body && (
            <span
                style={{ fontSize: 14.5, color: '#5B6B7F', marginTop: 8, textAlign: 'center', lineHeight: '22px' }}
            >
                {body}
            </span>
        )}
        {hint && (
            <span
                className="rounded-[9px]"
                style={{
                    marginTop: 16,
                    background: '#F7F9FC',
                    color: '#7C8794',
                    fontSize: 13,
                    padding: '11px 16px',
                    lineHeight: '19px',
                    textAlign: 'center',
                }}
            >
                {hint}
            </span>
        )}
    </div>
);

/**
 * 아직 보낼 경로가 없는 버튼.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 버튼을 지우지 않고 남기는가
 * ─────────────────────────────────────────────────────────────
 * 선물 발송·면담 요청·칭찬 전달·감사 메시지는 기획상 있어야 하는 동작인데
 * 서버에 그 API 가 아직 없습니다. 예전에는 눌리면 "발송했습니다" 토스트만
 * 띄웠습니다 — 아무 데도 안 갔는데 원장님은 보냈다고 믿고 손을 뗐습니다.
 * 화면이 하면 안 되는 유일한 일이 그것입니다.
 *
 * 그렇다고 버튼을 지우면 "그런 기능이 없는 서비스"가 되어, 나중에 붙었을 때
 * 아무도 찾지 않습니다. 그래서 자리는 지키되 **누를 수 없게** 두고
 * 왜 못 누르는지를 그 자리에서 밝힙니다.
 *
 * ⚠️ 서버 API 가 생기면 이 컴포넌트를 실제 버튼으로 바꾸세요.
 *    요청 내역은 BACKEND_HANDOFF_OPS_BRIEFING_ACTIONS.md 에 있습니다.
 */
export const OpsComingSoonButton = ({
    label,
    reason,
    style,
}: {
    label: string;
    /** 왜 아직 못 쓰는지 — 마우스를 올리면 보인다 */
    reason: string;
    style?: React.CSSProperties;
}) => (
    <button
        type="button"
        // 실제로 못 누르게 한다. cursor 만 바꾸면 눌러 보고 아무 일도 안 일어난다.
        disabled
        aria-disabled="true"
        title={`${reason} 준비되면 이 버튼이 열립니다.`}
        className="flex items-center justify-center rounded-[9px]"
        style={{
            height: 42,
            background: '#F4F6FA',
            border: `1px solid ${BORDER}`,
            color: '#A7B0BC',
            fontSize: 14.5,
            fontWeight: 600,
            cursor: 'not-allowed',
            gap: 7,
            ...style,
        }}
    >
        {label}
        <span
            className="rounded-[5px]"
            style={{
                background: '#E7EDF6',
                color: '#7C8794',
                fontSize: 10.5,
                fontWeight: 800,
                padding: '2px 6px',
                flex: 'none',
            }}
        >
            준비 중
        </span>
    </button>
);

/**
 * 목 데이터로 돌고 있음을 알리는 배지.
 * 실서버가 붙기 전 화면을 보여드릴 때, 숫자가 진짜라고 오해하면 안 된다.
 */
export const OpsMockBadge = () => (
    <div
        className="flex w-full items-center rounded-[10px]"
        style={{ background: '#FFF8E8', border: '1px solid #F3DFAE', padding: '9px 14px', gap: 8 }}
    >
        <span style={{ fontSize: 13 }}>🧪</span>
        <span style={{ fontSize: 12.5, color: '#8A5A00' }}>
            <b>예시 데이터</b>로 보고 계십니다. 서버 연동 전이라 숫자는 실제 운영 값이 아닙니다.
        </span>
    </div>
);
